import Category from '@shared/classes/category';
import DateUtils from '@shared/utils/date';
import EpgEvent from '@shared/classes/epgEvent';
import Rail from '@shared/classes/rail';
import { MainTimeline } from '@shared/types/interfaces/main';
import { selectCurrentParams } from '@shared/store/slices/router.slice';
import { selectSelection } from '@shared/store/slices/main.slice';
import { store } from '@shared/store/store';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TIMELINE_OFFSET } from '@shared/types/constants/main';

interface TimelineProps {
  category?: Category;
  filter?: Rail;
  loading?: boolean;
  rails?: Rail[];
  timelineRef: React.MutableRefObject<MainTimeline | null>;
  changeAsset: (id: string, index: number) => void;
  scheduleParamsWrite: () => void;
}

interface Chunk {
  start: Date;
  end: Date;
}

export default function useTimeline({
  category,
  filter,
  loading,
  rails,
  timelineRef,
  changeAsset,
  scheduleParamsWrite,
}: TimelineProps) {
  const currentParams = useAppSelector(selectCurrentParams);
  // Only EPG/timeline pages call this hook (see useMain.ts), so scoping the
  // `selection` subscription here keeps regular rail pages from re-rendering
  // on every horizontal move.
  const selection = useAppSelector(selectSelection);

  const [indicator, setIndicator] = useState<number | null>(null);
  const [timeline, setTimeline] = useState(() => {
    const initialDateTimestamp =
      currentParams?.category?.date?.start || Date.now();
    return (currentParams?.timeline ||
      DateUtils.getChunkFromDate(
        TIMELINE_OFFSET,
        new Date(initialDateTimestamp)
      )) as Chunk;
  });

  const loadDirection = useRef<string | null>(null);
  const nextChunk = useRef<Chunk | null>(null);
  const previousTimeline = useRef(timelineRef.current);
  const railsRef = useRef(rails);
  railsRef.current = rails;

  const loadInitialAsset = useRef(!!currentParams?.asset);
  const previousCategoryIdRef = useRef(category?.id);
  // Pending on mount when there's no restored asset, to preserve the
  // first-load "smart current-date position" behavior (current-date event,
  // not index 0) — category hasn't "changed" relative to its own initial
  // value, so this needs a separate seed from the category-diff check below.
  const pendingCategoryResetRef = useRef(!loadInitialAsset.current);
  const railsAtCategoryPendingRef = useRef(rails);
  const railsAtDirectionPendingRef = useRef(rails);

  const boundsAction = useCallback(
    (key: string) => {
      const chunk = getChunkFromInput(key);
      const handled = navigateToChunk(chunk) ?? false;
      if (handled) {
        loadDirection.current = key;
        railsAtDirectionPendingRef.current = railsRef.current;
      } else {
        loadDirection.current = null;
      }
      return handled;
    },
    [filter, category]
  );

  function getChunkFromInput(key: string) {
    const newDate = getShiftedDateFromInput(key);
    if (!newDate) return null;
    const categories = (filter?.data || []) as Category[];
    if (!categories.length) return null;
    const newChunk = DateUtils.getChunkFromDate(TIMELINE_OFFSET, newDate);
    if (!isChunkWithinLimits(newChunk, categories)) return null;
    return newChunk;
  }

  function getShiftedDateFromInput(key: string) {
    if (!timelineRef.current) return null;
    const date = new Date(timelineRef.current.start);
    const direction = key === 'LEFT' ? -1 : 1;
    date.setHours(date.getHours() + TIMELINE_OFFSET * direction);
    return date;
  }

  /**
   * Whether `rail` has at least one genuinely-fetched event, as opposed to
   * being entirely `EpgUtils.fillRailGaps` mocked placeholders — the shape
   * rails take while their GraphQL data is still loading for a new chunk.
   */
  function hasLoadedData(rail: Rail) {
    return (rail.data as EpgEvent[]).some((item) => !item?.mocked);
  }

  function isChunkWithinLimits(chunk: Chunk, categories: Category[]) {
    const leftLimit = categories[0]?.date?.start;
    const rightLimit = categories[categories.length - 1]?.date?.end;
    if (leftLimit !== undefined && chunk.start.getTime() < leftLimit) {
      return false;
    } else if (rightLimit !== undefined && chunk.start.getTime() > rightLimit) {
      return false;
    }
    return true;
  }

  function navigateToChunk(chunk: Chunk | null) {
    if (!chunk) return false;
    if (isWithinCurrentCategory(chunk.start)) {
      return navigateWithinCategory(chunk);
    } else {
      return navigateToNewCategory(chunk);
    }
  }

  function isWithinCurrentCategory(date: Date) {
    const start = category?.date?.start;
    const end = category?.date?.end;
    if (start === undefined || end === undefined) return false;
    return date.getTime() >= start && date.getTime() <= end;
  }

  function navigateWithinCategory(chunk: Chunk) {
    updateTimeline(chunk);
    return true;
  }

  function navigateToNewCategory(chunk: Chunk) {
    const filterId = filter?.id;
    if (!filterId) return false;
    const categoryIndex = getCategoryIndexFromChunk(chunk);
    if (categoryIndex === -1) return false;
    setTimeout(() => {
      changeAsset(filterId, categoryIndex);
      nextChunk.current = chunk;
    });
    return navigateWithinCategory(chunk);
  }

  function getCategoryIndexFromChunk(chunk: Chunk) {
    const categories = (filter?.data || []) as Category[];
    const categoryIndex = categories.findIndex(
      (item) =>
        item?.date?.start !== undefined &&
        item?.date?.end !== undefined &&
        chunk.start.getTime() >= item.date.start &&
        chunk.start.getTime() < item.date.end
    );
    return categoryIndex;
  }

  function updateTimeline(chunk: Chunk, fromRails = true) {
    try {
      if (fromRails) {
        previousTimeline.current = timelineRef.current;
      }
      timelineRef.current = {
        ...chunk,
        indicator,
        offset: TIMELINE_OFFSET,
        boundsAction,
        setIndicator,
      };
      setTimeline(chunk);
      scheduleParamsWrite();
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const initializeCategory = filter?.data && !currentParams?.category;
    if (initializeCategory) {
      const currentDateCategoryIndex = (filter?.data as Category[]).findIndex(
        (item) => DateUtils.isCurrentDate(item?.date?.start, item?.date?.end)
      );
      if (currentDateCategoryIndex > -1) {
        changeAsset(filter?.id, currentDateCategoryIndex);
      }
    }
  }, [filter]);

  useEffect(() => {
    if (loading) return;
    previousTimeline.current = null;

    // Change category from rails
    if (nextChunk.current) {
      updateTimeline(nextChunk.current);
      nextChunk.current = null;
    }
    // Change category from filter
    else {
      const categoryDate = new Date(category?.date?.start || Date.now());
      const currentDate = new Date();
      const isCategoryDateSameAsCurrent =
        currentDate.getDate() === categoryDate.getDate();
      const newDate = isCategoryDateSameAsCurrent ? currentDate : categoryDate;
      const newChunk = DateUtils.getChunkFromDate(TIMELINE_OFFSET, newDate);
      updateTimeline(newChunk, false);
    }
  }, [category]);

  /**
   * Refocuses the asset when the category (filter selection) genuinely
   * changes: first asset of the first rail, or the currently airing event
   * if the new category is today. Gated on `selection === filter?.id`
   * (rather than `loadDirection`, which belongs to the direction-based
   * effect below) since that's the direct signal for "the change came from
   * the filter rail" — during a timeline bounds-driven category crossing,
   * `selection` stays on the content rail the whole time.
   */
  useEffect(() => {
    if (category?.id !== previousCategoryIdRef.current) {
      previousCategoryIdRef.current = category?.id;
      if (loadInitialAsset.current) {
        loadInitialAsset.current = false; // There IS a restored asset
      } else if (selection === filter?.id) {
        pendingCategoryResetRef.current = true;
        railsAtCategoryPendingRef.current = rails;
      }
    }
    if (!pendingCategoryResetRef.current) return;
    if (loading) return;
    if (rails === railsAtCategoryPendingRef.current) return; // Not rebuilt yet
    // Keep the selected rail (e.g. set through `initialSelection`),
    // otherwise fall back to the first one. Read from the store, as the
    // selection may have been updated by a layout effect in this same commit
    const { selection: currentSelection } = store.getState().main;
    const rail =
      rails?.find((item) => item.id === currentSelection) || rails?.[0];
    if (!rail?.data?.length) return;
    if (!hasLoadedData(rail)) return; // Still mocked placeholders; wait for the real fetch
    pendingCategoryResetRef.current = false;
    const isCurrentCategory = DateUtils.isCurrentDate(
      category?.date?.start,
      category?.date?.end
    );
    const index = isCurrentCategory
      ? Math.max(
          rail.data.findIndex((item) =>
            DateUtils.isCurrentDate(item?.date?.start, item?.date?.end)
          ),
          0
        )
      : 0;
    changeAsset(rail.id, index);
  }, [category, rails, loading, selection, filter]);

  /**
   * Refocuses the asset when timeline bounds navigation shifts the chunk
   * (`resetLayout` in useMainKeyboard.ts zeroes hIndex/length but doesn't
   * pick a new asset). Lands on the last item of the currently selected
   * rail if the shift came from LEFT, or the first item if from RIGHT.
   */
  useEffect(() => {
    if (!loadDirection.current) return;
    if (loading) return;
    if (rails === railsAtDirectionPendingRef.current) return; // Not rebuilt yet
    const rail = rails?.find((item) => item.id === selection);
    if (!rail?.data?.length) return;
    if (!hasLoadedData(rail)) return; // Still mocked placeholders; wait for the real fetch
    const index = loadDirection.current === 'LEFT' ? rail.data.length - 1 : 0;
    loadDirection.current = null;
    changeAsset(selection, index);
  }, [rails, loading, selection]);

  return useMemo(() => {
    return timeline
      ? {
          ...timeline,
          indicator,
          offset: TIMELINE_OFFSET,
          boundsAction,
          setIndicator,
        }
      : null;
  }, [indicator, timeline]);
}
